import {
  Box,
  Button,
  Checkbox,
  FormControl,
  FormControlLabel,
  Grid,
  IconButton,
  Radio,
  RadioGroup,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import { CustomSwitch } from "components/shared/custom-switch";
import { FC, useContext, useEffect, useMemo, useRef, useState } from "react";
import { neutral, primary } from "style/color";
import { ReactComponent as EyeOpen } from "./../../../assets/Icons/eye-open.svg";
import { GrantRequestInput } from "types/grant-request";
import AddIcon from "@mui/icons-material/Add";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import { Charity } from "types/charity";
import { AuthContext } from "providers/userProvider";
import { AuthData } from "types/auth";
import { useGetFundQuery } from "hooks/use-services/fund.service";
import awsExports from "aws-exports";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import { isValidEmail, EMAIL_VALIDATION_ERROR_MESSAGE } from "utils/validate-email";

export interface DonorSelection {
  memberId: number;
  memberName: string;
  address: string;
  recognition: boolean;
  recommendation: boolean;
  recognitionWithAddress: boolean;
  isCustom: boolean;
}

interface GrantAcknowledgementDetailsProps {
  grantRequest: GrantRequestInput;
  charity: Charity;
  handleGrantRequestChange: (key: string, value: any) => void;
  onPreviewGrantLetterPdf?: () => void | Promise<void>;
  onThirdPartyEmailsValidationChange?: (hasInvalidEmail: boolean) => void;
}

export const GrantAcknowledgementDetails: FC<
  GrantAcknowledgementDetailsProps
> = ({
  grantRequest,
  charity,
  handleGrantRequestChange,
  onPreviewGrantLetterPdf,
  onThirdPartyEmailsValidationChange,
}) => {
  const { selectedAccount, userData } = useContext<AuthData>(AuthContext);
  const {
    data: fundAccount,
    isLoading,
    error,
  } = useGetFundQuery(
    selectedAccount?.fundAccountId || 0,
    !!selectedAccount?.fundAccountId // Only run query if we have a valid ID
  );

  const personalName = useMemo(
    () => `${userData?.user?.firstName || ""} ${userData?.user?.lastName || ""}`.trim(),
    [userData?.user?.firstName, userData?.user?.lastName]
  );
  const personalAddress = useMemo(
    () =>
      `${userData?.user?.addressLine1 || ""}${
        userData?.user?.addressLine2 ? `, ${userData?.user?.addressLine2}` : ""
      }, ${userData?.user?.city || ""}, ${userData?.user?.state || ""} ${
        userData?.user?.zip || ""
      }`.trim(),
    [
      userData?.user?.addressLine1,
      userData?.user?.addressLine2,
      userData?.user?.city,
      userData?.user?.state,
      userData?.user?.zip,
    ]
  );

  const [grantFromFund, setGrantFromFund] = useState<boolean>(true);
  const [grantFromPersonalEnabled, setGrantFromPersonalEnabled] =
    useState<boolean>(false);
  const [isAnonymous, setIsAnonymous] = useState<boolean>(
    grantRequest.acknowledgement.anonymous
  );
  const [grantFromPersonalNames, setGrantFromPersonalNames] = useState<string[]>(
    personalName ? [personalName] : []
  );
  const [grantFromCustomEnabled, setGrantFromCustomEnabled] =
    useState<boolean>(false);
  const [grantFromCustomName, setGrantFromCustomName] = useState<string>("");

  const [recognizedFundName, setRecognizedFundName] = useState<boolean>(true);
  const [recognizedPersonalName, setRecognizedPersonalName] = useState<boolean>(false);
  const [recognizedPersonalNames, setRecognizedPersonalNames] = useState<string[]>(
    personalName ? [personalName] : []
  );
  const [recognizedCustomEnabled, setRecognizedCustomEnabled] =
    useState<boolean>(false);
  const [recognizedCustomName, setRecognizedCustomName] = useState<string>("");

  const [mailingAddressChoice, setMailingAddressChoice] = useState<"yes" | "no">(
    grantRequest.acknowledgement.showDonorAddress ||
      grantRequest.acknowledgement.showDonorAdress
      ? "yes"
      : "no"
  );
  const [addDedication, setAddDedication] = useState<boolean>(
    !!(
      grantRequest.acknowledgement.inHonorOf ||
      grantRequest.acknowledgement.inMemoryOf
    )
  );

  const [donorSelections, setDonorSelections] = useState<DonorSelection[]>([]);
  const wasAnonymousRef = useRef(isAnonymous);
  const hasRecognitionSelection =
    recognizedFundName || recognizedPersonalName || recognizedCustomEnabled;
  const personalNameOptions = useMemo(
    () =>
      [personalName, ...donorSelections.map((member) => member.memberName)]
        .filter((name): name is string => Boolean(name))
        .filter((name, index, self) => self.indexOf(name) === index),
    [personalName, donorSelections]
  );

  useEffect(() => {
    if (fundAccount?.fundAccountMembers) {
      const newSelections = fundAccount.fundAccountMembers
        .filter(
          (member) => member.user.sub !== awsExports.neta_super_admin_user_sub
        )
        .map((member) => ({
          memberId: member.user.userId ?? 0,
          memberName: `${member.user.firstName || ""} ${
            member.user.lastName || ""
          }`.trim(),
          address: `${member.user?.addressLine1}${
            member.user?.addressLine2 ? `, ${member.user?.addressLine2},` : `, `
          } ${member.user?.city}, ${member.user?.state}, ${member.user?.zip}`,
          recognition: false,
          recommendation: false,
          recognitionWithAddress: false,
          isCustom: false,
        }));
      setDonorSelections(newSelections);
    }
  }, [fundAccount?.fundAccountMembers]);

  const handleAcknowledgementChange = (key: string, value: any) => {
    handleGrantRequestChange("acknowledgement", {
      ...grantRequest.acknowledgement,
      [key]: value,
    });
  };

  useEffect(() => {
    setGrantFromPersonalNames((previous) =>
      previous.filter((name) => personalNameOptions.includes(name))
    );
  }, [personalNameOptions]);

  useEffect(() => {
    setRecognizedPersonalNames((previous) =>
      previous.filter((name) => personalNameOptions.includes(name))
    );
  }, [personalNameOptions]);

  useEffect(() => {
    const recommendationEntries: DonorSelection[] = [];
    if (!isAnonymous && grantFromPersonalEnabled) {
      grantFromPersonalNames
        .filter((name) => name.trim())
        .forEach((name) => {
          recommendationEntries.push({
            memberId: -1,
            memberName: name.trim(),
            address: "",
            recognition: false,
            recommendation: true,
            recognitionWithAddress: false,
            isCustom: false,
          });
        });
    }
    if (!isAnonymous && grantFromCustomEnabled && grantFromCustomName.trim()) {
      recommendationEntries.push({
        memberId: -2,
        memberName: grantFromCustomName.trim(),
        address: "",
        recognition: false,
        recommendation: true,
        recognitionWithAddress: false,
        isCustom: true,
      });
    }

    const recognitionEntries: DonorSelection[] = [];
    if (!isAnonymous && recognizedPersonalName) {
      recognizedPersonalNames
        .filter((name) => name.trim())
        .forEach((name) => {
          recognitionEntries.push({
            memberId: -3,
            memberName: name.trim(),
            address: "",
            recognition: true,
            recommendation: false,
            recognitionWithAddress: false,
            isCustom: false,
          });
        });
    }
    if (
      !isAnonymous &&
      recognizedCustomEnabled &&
      recognizedCustomName.trim()
    ) {
      recognitionEntries.push({
        memberId: -4,
        memberName: recognizedCustomName.trim(),
        address: "",
        recognition: true,
        recommendation: false,
        recognitionWithAddress: false,
        isCustom: true,
      });
    }

    const donorSelections = [...recommendationEntries, ...recognitionEntries].filter(
      (entry, index, self) =>
        self.findIndex(
          (other) =>
            other.memberName === entry.memberName &&
            other.recommendation === entry.recommendation &&
            other.recognition === entry.recognition
        ) === index
    );

    const justLeftAnonymous = wasAnonymousRef.current && !isAnonymous;
    wasAnonymousRef.current = isAnonymous;

    handleGrantRequestChange("acknowledgement", {
      ...grantRequest.acknowledgement,
      showFundName: !isAnonymous && (grantFromFund || recognizedFundName),
      showFundNameInRecommendation: !isAnonymous && grantFromFund,
      showFundNameInRecognition: !isAnonymous && recognizedFundName,
      anonymous: isAnonymous,
      donorSelections,
      ...(isAnonymous
        ? {
            showDonorAddress: false,
            showDonorAdress: false,
            donorAddress: "",
            visibility: false,
          }
        : justLeftAnonymous
          ? { visibility: true }
          : {}),
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    isAnonymous,
    grantFromFund,
    grantFromPersonalEnabled,
    grantFromPersonalNames,
    grantFromCustomEnabled,
    grantFromCustomName,
    recognizedFundName,
    recognizedPersonalName,
    recognizedPersonalNames,
    recognizedCustomEnabled,
    recognizedCustomName,
  ]);

  const handleChange = (index: number, value: string) => {
    const emails = [...grantRequest.acknowledgement.thirdPartiesEmails];
    emails[index] = value;
    handleGrantRequestChange("acknowledgement", {
      ...grantRequest.acknowledgement,
      thirdPartiesEmails: emails,
    });
  };

  const removeEmail = (index: number) => {
    const emails = grantRequest.acknowledgement.thirdPartiesEmails.filter(
      (_, i) => i !== index
    );
    handleGrantRequestChange("acknowledgement", {
      ...grantRequest.acknowledgement,
      thirdPartiesEmails: emails,
    });
  };

  const thirdPartiesEmails = grantRequest.acknowledgement.thirdPartiesEmails;
  useEffect(() => {
    const hasInvalidEmail = thirdPartiesEmails.some(
      (email) => email && !isValidEmail(email)
    );
    onThirdPartyEmailsValidationChange?.(hasInvalidEmail);
  }, [thirdPartiesEmails, onThirdPartyEmailsValidationChange]);

  return (
    <Grid container rowSpacing={0} columnSpacing={2}>
      <Grid item xs={12}>
        <Typography
          variant="bodyCopyRegular"
          color={neutral[600]}
          sx={{ mb: 0 }}
          paragraph
        >
          Grant details will be included in a letter sent to the charity with each grant.
          There are two sections; the first is for who the grant is from, and the second one is who to recognize and how.
        </Typography>
      </Grid>

      <Grid item xs={12} sx={{ mt: 1 }}>
        <FormControlLabel
          control={
            <CustomSwitch
              onChange={(event) => {
                const nextAnonymous = event.target.checked;
                if (nextAnonymous) {
                  setGrantFromFund(true);
                  setGrantFromPersonalEnabled(false);
                  setGrantFromPersonalNames(personalName ? [personalName] : []);
                  setGrantFromCustomEnabled(false);
                  setGrantFromCustomName("");
                  setRecognizedFundName(true);
                  setRecognizedPersonalName(false);
                  setRecognizedPersonalNames(personalName ? [personalName] : []);
                  setRecognizedCustomEnabled(false);
                  setRecognizedCustomName("");
                  setMailingAddressChoice("no");
                }
                setIsAnonymous(nextAnonymous);
              }}
              checked={isAnonymous}
            />
          }
          label="Make this grant anonymous"
        />
      </Grid>
      {!isAnonymous && (
        <>
          <Grid item xs={12} sx={{ mt: 2 }}>
            <Typography variant="subtitle3" color={neutral[900]} sx={{ mb: 1 }}>
              Who is this grant from?
            </Typography>
          </Grid>
          <Grid item xs={12} sx={{ mt: 1 }}>
            <FormControlLabel
              control={
                <Checkbox
                  checked={grantFromFund}
                  onChange={(event) => setGrantFromFund(event.target.checked)}
                />
              }
              label={selectedAccount?.fundAccountName || "Fund name"}
            />
            <FormControlLabel
              control={
                <Checkbox
                  checked={grantFromPersonalEnabled}
                  onChange={(event) => {
                    const checked = event.target.checked;
                    setGrantFromPersonalEnabled(checked);
                    if (checked && grantFromPersonalNames.length === 0) {
                      setGrantFromPersonalNames(
                        personalNameOptions.length > 0 ? [personalNameOptions[0]] : []
                      );
                    }
                  }}
                />
              }
              label="Use my personal name"
            />
            {grantFromPersonalEnabled && (
              <Box sx={{ ml: 4, mt: 1, mb: 1 }}>
                {personalNameOptions.map((name) => (
                  <FormControlLabel
                    key={name}
                    control={
                      <Checkbox
                        checked={grantFromPersonalNames.includes(name)}
                        onChange={(event) => {
                          const checked = event.target.checked;
                          setGrantFromPersonalNames((previous) =>
                            checked
                              ? [...previous, name].filter(
                                  (entry, index, self) => self.indexOf(entry) === index
                                )
                              : previous.filter((entry) => entry !== name)
                          );
                        }}
                      />
                    }
                    label={name}
                  />
                ))}
              </Box>
            )}
            <FormControlLabel
              control={
                <Checkbox
                  checked={grantFromCustomEnabled}
                  onChange={(event) => setGrantFromCustomEnabled(event.target.checked)}
                />
              }
              label="Use a custom name"
            />
            {grantFromCustomEnabled && (
              <Box sx={{ ml: 4, mt: 1, maxWidth: 420 }}>
                <TextField
                  fullWidth
                  size="small"
                  placeholder="Enter desired name(s)"
                  value={grantFromCustomName}
                  onChange={(event) => setGrantFromCustomName(event.target.value)}
                />
              </Box>
            )}
          </Grid>
          <Grid item xs={12} sx={{ mt: 3 }}>
            <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
              <Typography variant="subtitle3" color={neutral[900]}>
                Who should be recognized?
              </Typography>
              <Tooltip
                title="This is the name the charity will see on the grant letter."
                slotProps={{
                  tooltip: {
                    sx: { fontSize: "calc(0.75rem + 2px)" },
                  },
                }}
              >
                <InfoOutlinedIcon sx={{ color: neutral[600], fontSize: 18 }} />
              </Tooltip>
            </Box>
          </Grid>
          <Grid item xs={12} sx={{ mt: 1 }}>
            <FormControlLabel
              control={
                <Checkbox
                  checked={recognizedFundName}
                  onChange={(event) => setRecognizedFundName(event.target.checked)}
                />
              }
              label={selectedAccount?.fundAccountName || "Fund name"}
            />
            <FormControlLabel
              control={
                <Checkbox
                  checked={recognizedPersonalName}
                  onChange={(event) => {
                    const checked = event.target.checked;
                    setRecognizedPersonalName(checked);
                    if (checked && recognizedPersonalNames.length === 0) {
                      setRecognizedPersonalNames(
                        personalNameOptions.length > 0 ? [personalNameOptions[0]] : []
                      );
                    }
                  }}
                />
              }
              label="Use my personal name"
            />
            <FormControlLabel
              control={
                <Checkbox
                  checked={recognizedCustomEnabled}
                  onChange={(event) =>
                    setRecognizedCustomEnabled(event.target.checked)
                  }
                />
              }
              label="Use a custom name"
            />
            {recognizedPersonalName && (
              <Box sx={{ ml: 4, mt: 1, mb: 1 }}>
                {personalNameOptions.map((name) => (
                  <FormControlLabel
                    key={name}
                    control={
                      <Checkbox
                        checked={recognizedPersonalNames.includes(name)}
                        onChange={(event) => {
                          const checked = event.target.checked;
                          setRecognizedPersonalNames((previous) =>
                            checked
                              ? [...previous, name].filter(
                                  (entry, index, self) => self.indexOf(entry) === index
                                )
                              : previous.filter((entry) => entry !== name)
                          );
                        }}
                      />
                    }
                    label={name}
                  />
                ))}
              </Box>
            )}
            {recognizedCustomEnabled && (
              <Box sx={{ ml: 4, mt: 1, maxWidth: 420 }}>
                <TextField
                  fullWidth
                  size="small"
                  placeholder="Enter desired name(s)"
                  value={recognizedCustomName}
                  onChange={(event) => setRecognizedCustomName(event.target.value)}
                />
              </Box>
            )}
          </Grid>

          {hasRecognitionSelection && (
            <Grid item xs={12} sx={{ mt: 3 }}>
              <Typography variant="subtitle3" color={neutral[900]} sx={{ mb: 1 }}>
                Should we include a mailing address?
              </Typography>
              <RadioGroup
                row
                value={mailingAddressChoice}
                onChange={(event) => {
                  const nextChoice = event.target.value as "yes" | "no";
                  setMailingAddressChoice(nextChoice);
                  const showAddress = nextChoice === "yes";
                  handleGrantRequestChange("acknowledgement", {
                    ...grantRequest.acknowledgement,
                    showDonorAddress: showAddress,
                    showDonorAdress: showAddress,
                    donorAddress: showAddress
                      ? grantRequest.acknowledgement.donorAddress || personalAddress
                      : "",
                  });
                }}
              >
                <FormControlLabel value="no" control={<Radio />} label="No" />
                <FormControlLabel value="yes" control={<Radio />} label="Yes" />
              </RadioGroup>
              {mailingAddressChoice === "yes" && (
                <Grid item xs={12} sm={8} sx={{ mt: 1 }}>
                  <TextField
                    fullWidth
                    multiline
                    minRows={3}
                    value={grantRequest.acknowledgement.donorAddress || ""}
                    onChange={(event) =>
                      handleAcknowledgementChange("donorAddress", event.target.value)
                    }
                  />
                </Grid>
              )}
            </Grid>
          )}
        </>
      )}

      <Grid item xs={12} sx={{ mt: 4 }}>
        <Typography variant="subtitle3" color={neutral[900]}>
          Add a Dedication
        </Typography>
        <Typography variant="bodyCopyRegular" color={neutral[900]} sx={{ mt: 1 }} paragraph>
          Would you like to dedicate this grant to someone living (in honor of)
          and/or to someone that&apos;s passed (in memory of)?
        </Typography>
        <RadioGroup
          row
          value={addDedication ? "yes" : "no"}
          onChange={(event) => {
            const shouldAdd = event.target.value === "yes";
            setAddDedication(shouldAdd);
            if (!shouldAdd) {
              handleGrantRequestChange("acknowledgement", {
                ...grantRequest.acknowledgement,
                inHonorOf: "",
                inMemoryOf: "",
              });
            }
          }}
          sx={{ mt: 1 }}
        >
          <FormControlLabel value="yes" control={<Radio />} label="Yes" />
          <FormControlLabel value="no" control={<Radio />} label="No" />
        </RadioGroup>
      </Grid>

      {addDedication && (
        <>
          <Grid item xs={12} sm={8} sx={{ mt: 2 }}>
            <Typography variant="subtitle3" color={neutral[900]}>
              In honor of
            </Typography>
            <Typography variant="bodyCopyRegular" color={neutral[600]} sx={{ mb: 1 }} paragraph>
              Enter one or more names and/or addresses.
            </Typography>
            <FormControl fullWidth>
              <TextField
                multiline
                rows={4}
                value={grantRequest.acknowledgement.inHonorOf}
                onChange={(event) =>
                  handleAcknowledgementChange("inHonorOf", event.target.value)
                }
                placeholder="Enter one or more names and/or addresses."
              />
            </FormControl>
          </Grid>
          <Grid item xs={12} sm={8} sx={{ mt: 2 }}>
            <Typography variant="subtitle3" color={neutral[900]}>
              In memory of
            </Typography>
            <Typography variant="bodyCopyRegular" color={neutral[600]} sx={{ mb: 1 }} paragraph>
              Enter one or more names and/or addresses.
            </Typography>
            <FormControl fullWidth>
              <TextField
                multiline
                rows={4}
                value={grantRequest.acknowledgement.inMemoryOf}
                onChange={(event) =>
                  handleAcknowledgementChange("inMemoryOf", event.target.value)
                }
                placeholder="Enter one or more names and/or addresses."
              />
            </FormControl>
          </Grid>
        </>
      )}
      <Grid item xs={12} sx={{ mt: 3 }}>
        <Typography variant="subtitle3" color={neutral[900]}>
        Would you like us to notify someone about this grant? (third parties)
        </Typography>
        <Typography
          variant="bodyCopyRegular"
          color={neutral[600]}
          sx={{ mb: 0 }}
          paragraph
        >
          The charity receiving the grant will be notified by a standard communication. If you wish to notify other parties of this grant, add their email(s) below and they will get a notification.
        </Typography>
      </Grid>
      {grantRequest.acknowledgement.thirdPartiesEmails.map((email, index) => (
        <>
          <Grid sx={{ mt: 2 }} item xs={8} sm={8}>
            <FormControl fullWidth error={!!email && !isValidEmail(email)}>
              <TextField
                size="small"
                fullWidth
                name="email"
                value={email}
                onChange={(event) => handleChange(index, event.target.value)}
                variant="outlined"
                type="text"
                placeholder="Enter email"
                error={!!email && !isValidEmail(email)}
                helperText={
                  email && !isValidEmail(email)
                    ? EMAIL_VALIDATION_ERROR_MESSAGE
                    : ""
                }
              />
            </FormControl>
          </Grid>
          {grantRequest.acknowledgement.thirdPartiesEmails.length !== 1 && (
            <Grid sx={{ mt: 2 }} item xs={1} sm={1}>
              <IconButton
                onClick={(event) => removeEmail(index)}
                sx={{
                  backgroundColor: "#fff",
                  color: primary[500],
                  border: "1px solid #0B3A81",
                  "&:hover": {
                    backgroundColor: primary[500],
                    color: "#fff",
                  },
                }}
              >
                <DeleteOutlineIcon />
              </IconButton>
            </Grid>
          )}
        </>
      ))}
      <Grid sx={{ mt: 2 }} item xs={1} sm={1}>
        <IconButton
          onClick={(event) =>
            handleChange(
              grantRequest.acknowledgement.thirdPartiesEmails.length,
              ""
            )
          }
          sx={{
            backgroundColor: "#fff",
            color: primary[500],
            border: "1px solid #0B3A81",
            "&:hover": {
              backgroundColor: primary[500],
              color: "#fff",
            },
          }}
          disabled={
            grantRequest.acknowledgement.thirdPartiesEmails[
              grantRequest.acknowledgement.thirdPartiesEmails.length - 1
            ] === ""
          }
        >
          <AddIcon />
        </IconButton>
      </Grid>
      {!isAnonymous && (
        <>
          <Grid sx={{ mt: 2 }} item xs={12}>
            <Typography variant="subtitle3" color={neutral[900]}>
              Visibility*
            </Typography>
            <Typography
              variant="bodyCopyRegular"
              color={neutral[600]}
              sx={{ mb: 0 }}
              paragraph
            >
              We do not control how the recipient of the grant will publicly
              recognize you (or not), but we can tell them your preference.
            </Typography>
          </Grid>
          <Grid sx={{ mt: 1 }} item xs={12}>
            <RadioGroup
              onChange={(event) =>
                handleAcknowledgementChange(
                  "visibility",
                  event.target.value === "true"
                )
              }
              value={grantRequest.acknowledgement.visibility}
              name="visibility"
            >
              <FormControlLabel
                value={true}
                control={<Radio />}
                label="Yes, I agree to have the acknowledgement details indicated above shared publicly by the recipient"
              />
              <FormControlLabel
                value={false}
                control={<Radio />}
                label="No, I prefer to not have my details shared publicly"
              />
            </RadioGroup>
          </Grid>
        </>
      )}
      <Grid sx={{ my: 2 }} item xs={8}>
        <Button
          size="medium"
          variant="outlined"
          startIcon={<EyeOpen />}
          onClick={() => void onPreviewGrantLetterPdf?.()}
          disabled={!onPreviewGrantLetterPdf}
        >
          Preview the Grant letter
        </Button>
      </Grid>
      {isLoading && (
        <Grid item xs={12} sx={{ mt: 2 }}>
          <Typography>Loading members...</Typography>
        </Grid>
      )}
      {!!error && (
        <Grid item xs={12} sx={{ mt: 2 }}>
          <Typography color="error">Error loading members </Typography>
        </Grid>
      )}
    </Grid>
  );
};
